import type React from "react";
import { ExternalLink } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { Badge } from "../client/components/ui/badge";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import type { OfferVM, FirmVM } from "./lib/types";
import { effectivePrice } from "./lib/derive";
import { buildAffiliateUrl } from "./lib/affiliate";
import { formatUsd } from "./lib/format";
import { drawdownTypeLabel, ratingClass } from "./lib/labels";
import { bestOfferIdsByCriterion } from "./lib/bestValue";

interface CompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  offers: OfferVM[]; // the 2..4 selected offers
  firms: Map<string, FirmVM>;
  now: Date;
}

export default function ComparatorCompareDialog({
  open,
  onOpenChange,
  offers,
  firms,
  now,
}: CompareDialogProps) {
  const t = useTranslation();

  // Only compare offers whose firm is resolvable.
  const rows = offers.filter((o) => firms.get(o.firmId));
  const best = bestOfferIdsByCriterion(rows, firms, now);

  function CriterionRow({
    label,
    render,
    highlightSet,
  }: {
    label: string;
    render: (o: OfferVM) => React.ReactNode;
    highlightSet?: Set<string>;
  }) {
    return (
      <tr className="border-t border-border">
        <td className="py-2 pr-4 font-medium text-muted-foreground">{label}</td>
        {rows.map((o) => {
          const isBest = highlightSet?.has(o.id) ?? false;
          const value = render(o);
          return (
            <td key={o.id} className="py-2 px-3 font-mono">
              {isBest ? (
                <span>
                  <span className="text-emerald-500 font-semibold">{value}</span>
                  <Badge className="ml-1 bg-emerald-500/15 text-emerald-500 text-[9px]">
                    {t.comparator.bestValue}
                  </Badge>
                </span>
              ) : (
                value
              )}
            </td>
          );
        })}
      </tr>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>{t.comparator.compareTitle}</DialogTitle>
        </DialogHeader>

        <table className="w-full text-sm">
          <thead>
            <tr>
              <th />
              {rows.map((o) => {
                const firm = firms.get(o.firmId);
                const url = firm ? buildAffiliateUrl(o, firm) : null;
                return (
                  <th key={o.id} className="px-3 pb-3 text-left align-bottom">
                    <div className="font-medium text-foreground">
                      {firm?.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {o.planName}
                    </div>
                    <Badge variant="outline" className="mt-1 font-mono">
                      {o.accountSizeK}k
                    </Badge>
                    <div className="mt-2">
                      {url ? (
                        <a href={url} target="_blank" rel="sponsored nofollow noopener noreferrer">
                          <Button size="sm">
                            {t.comparator.startChallenge}
                            <ExternalLink className="ml-1 h-3 w-3" />
                          </Button>
                        </a>
                      ) : (
                        <Button size="sm" disabled>
                          {t.comparator.startChallenge}
                        </Button>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <CriterionRow
              label={t.comparator.colPrice}
              render={(o) => formatUsd(effectivePrice(o, now))}
              highlightSet={best.price}
            />
            <CriterionRow
              label={t.comparator.total}
              render={(o) =>
                formatUsd(effectivePrice(o, now) + o.activationFeeUsd)
              }
              highlightSet={best.total}
            />
            <CriterionRow
              label={t.comparator.colActivation}
              render={(o) =>
                o.activationFeeUsd === 0
                  ? t.comparator.free
                  : formatUsd(o.activationFeeUsd)
              }
              highlightSet={best.activation}
            />
            <CriterionRow
              label={t.comparator.colBilling}
              render={(o) =>
                o.billingType === "one_time"
                  ? t.comparator.billingOneTime
                  : t.comparator.billingMonthly
              }
            />
            <CriterionRow
              label={t.comparator.colDrawdown}
              render={(o) =>
                o.drawdownUsd != null
                  ? formatUsd(o.drawdownUsd) +
                    (o.drawdownType
                      ? " " + drawdownTypeLabel(t, o.drawdownType)
                      : "")
                  : "—"
              }
              highlightSet={best.drawdown}
            />
            <CriterionRow
              label={t.comparator.colTarget}
              render={(o) =>
                o.profitTargetUsd != null ? formatUsd(o.profitTargetUsd) : "—"
              }
              highlightSet={best.profitTarget}
            />
            <CriterionRow
              label={t.comparator.colRoundTrip}
              render={(o) => (
                <span className="font-mono">
                  ${o.roundTripMicroUsd ?? "—"} / ${o.roundTripMiniUsd ?? "—"}
                </span>
              )}
              highlightSet={
                new Set([...best.roundTripMicro, ...best.roundTripMini])
              }
            />
            <CriterionRow
              label={t.comparator.colRating}
              render={(o) => {
                const firm = firms.get(o.firmId);
                const rating = firm?.rating ?? null;
                return (
                  <span className={`font-mono ${ratingClass(rating)}`}>
                    {rating ?? "—"}
                  </span>
                );
              }}
              highlightSet={best.rating}
            />
            <CriterionRow
              label={t.comparator.colPlatforms}
              render={(o) => (
                <div className="flex flex-wrap gap-1">
                  {o.platforms.map((p) => (
                    <Badge key={p} variant="outline" className="text-[10px]">
                      {p}
                    </Badge>
                  ))}
                </div>
              )}
            />
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}
